<template>
    <div class="logo-carousel-container">
        <span v-if="isClipped" class="clip-boundary left" :style="clipBoundaryStyle" />
        <span v-if="isClipped" class="clip-boundary right" :style="clipBoundaryStyle" />
        <div
            :class="['logo-carousel-shell', overflowClass]"
            @mouseenter="hoverActive = true"
            @mouseleave="hoverActive = false"
        >
            <div ref="track" class="logo-carousel-track">
                <div
                    v-for="repeatIndex in repeatCount"
                    :key="repeatIndex"
                    class="logo-carousel-sequence"
                >
                    <div
                        v-for="(logo, logoIndex) in section.logos"
                        :key="`${repeatIndex}-${logoIndex}`"
                        class="logo-carousel-item"
                        :style="{ '--logo-max-height': logoMaxHeight(logo) }"
                    >
                        <div
                            :class="['logo-box', unifiedClass, { gradient: isGradientBg }]"
                            :style="{ '--density': densityValue }"
                        >
                            <slot :logo="logo" :index="logoIndex" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import gsap from "gsap";
import { mapGetters } from "vuex";

const SPEED_CHART = {
    SLOW: 28,
    MEDIUM: 48,
    FAST: 96,
};

export default {
    name: "LogoCarousel",
    props: ["section"],
    data() {
        return {
            hoverActive: false,
            marqueeOffsetPx: 0,
            sequenceWidth: 0,
            marqueeCycleWidth: 0,
            repeatCount: 8,
            gsapTickerCallback: null,
            setTrackX: null,
            onWindowResize: null,
        };
    },
    computed: {
        ...mapGetters("design", ["resolveColor", "colorPalette"]),
        logoStyle() {
            return this.section?.logoStyle || {};
        },
        overflowClass() {
            const style = this.logoStyle.overflowStyle || "FULL_WIDTH";
            if (style === "FADED") return "overflow-faded";
            if (style === "CLIPPED") return "overflow-clipped";
            return "overflow-full-width";
        },
        unifiedClass() {
            if (this.logoStyle.logoHoverInteraction === "COLOR_REVEAL") return null;
            return this.logoStyle.unified ? "unified" : null;
        },
        densityValue() {
            return this.logoStyle.density ?? null;
        },
        isGradientBg() {
            return (
                this.section.backgroundSettings?.selectedBackgroundStyleId !== null &&
                this.section.backgroundSettings?.selectedBackgroundStyleId > 1
            );
        },
        baseSpeedPxPerSecond() {
            const speed = this.logoStyle.animationSpeed || "MEDIUM";
            return SPEED_CHART[speed] ?? SPEED_CHART.MEDIUM;
        },
        isClipped() {
            return (this.logoStyle.overflowStyle || "FULL_WIDTH") === "CLIPPED";
        },
        clipBoundaryStyle() {
            const colorData = this.logoStyle.clippedLineColor?.text?.idle;
            if (!colorData) return null;
            const color = Number.isFinite(colorData.paletteColorIndex)
                ? this.colorPalette?.colors?.[colorData.paletteColorIndex] || colorData.color
                : colorData.color;
            return color ? { background: color } : null;
        },
    },
    watch: {
        "section.logos": {
            deep: true,
            handler() {
                this.$nextTick(() => this.refreshSequenceWidth());
            },
        },
    },
    mounted() {
        this.$nextTick(() => {
            this.initializeTrackSetter();
            this.refreshSequenceWidth();
            this.startMarqueeLoop();
        });
        this.onWindowResize = () => this.refreshSequenceWidth();
        window.addEventListener("resize", this.onWindowResize);
    },
    beforeUnmount() {
        if (this.onWindowResize) {
            window.removeEventListener("resize", this.onWindowResize);
        }
        this.stopMarqueeLoop();
    },
    methods: {
        logoMaxHeight(logo) {
            const w = parseFloat(logo?.width);
            const h = parseFloat(logo?.height);
            return h > w ? "64px" : "32px";
        },
        initializeTrackSetter() {
            if (!this.$refs.track) return;
            this.setTrackX = gsap.quickSetter(this.$refs.track, "x", "px");
            gsap.set(this.$refs.track, { x: 0, force3D: true });
        },
        applyTrackOffset() {
            if (!this.setTrackX) return;
            this.setTrackX(-this.marqueeOffsetPx);
        },
        refreshSequenceWidth() {
            const firstSequence = this.$refs.track?.querySelector(".logo-carousel-sequence");
            if (!firstSequence) return;
            const gap = parseFloat(window.getComputedStyle(this.$refs.track).columnGap) || 0;
            const width = (firstSequence.offsetWidth || 0) + gap;
            if (!width) return;
            const cycleRepeatCount = this.resolveCycleRepeatCount(width);
            this.sequenceWidth = width;
            this.marqueeCycleWidth = width * cycleRepeatCount;
            this.marqueeOffsetPx = this.marqueeCycleWidth > 0
                ? this.marqueeOffsetPx % this.marqueeCycleWidth
                : 0;
            this.applyTrackOffset();
            this.repeatCount = Math.max(4, cycleRepeatCount + Math.ceil(window.innerWidth / this.sequenceWidth) + 2);
        },
        resolveCycleRepeatCount(sequenceWidth) {
            const safeWidth = Number(sequenceWidth) || 0;
            if (safeWidth <= 0) return 1;
            return Math.max(1, Math.ceil(window.innerWidth / safeWidth));
        },
        getCurrentSpeedPxPerSecond() {
            const baseSpeed = this.baseSpeedPxPerSecond;
            const hover = this.logoStyle.sectionHoverInteraction || "NONE";
            if (hover === "PAUSE" && this.hoverActive) return 0;
            if (hover === "SLOW_DOWN" && this.hoverActive) return baseSpeed * 0.45;
            return baseSpeed;
        },
        startMarqueeLoop() {
            if (this.gsapTickerCallback) return;
            let lastTime = gsap.ticker.time;
            this.gsapTickerCallback = () => {
                const now = gsap.ticker.time;
                const delta = Math.max(0, now - lastTime);
                lastTime = now;
                const speed = this.getCurrentSpeedPxPerSecond();
                if (this.marqueeCycleWidth > 0 && speed > 0) {
                    this.marqueeOffsetPx = (this.marqueeOffsetPx + speed * delta) % this.marqueeCycleWidth;
                    this.applyTrackOffset();
                }
            };
            gsap.ticker.add(this.gsapTickerCallback);
        },
        stopMarqueeLoop() {
            if (!this.gsapTickerCallback) return;
            gsap.ticker.remove(this.gsapTickerCallback);
            this.gsapTickerCallback = null;
        },
    },
};
</script>
